import React from "react";

import { describeListingObject } from "../utils/listingHelpers";
import ListingEditorActions from "./ListingEditorActions";
import ListingEditorNav from "./ListingEditorNav";
import ListingEditorPanel from "./ListingEditorPanel";
import ListingAmenitiesSection from "./sections/ListingAmenitiesSection";
import ListingBasicInfoSection from "./sections/ListingBasicInfoSection";
import ListingDescriptionSection from "./sections/ListingDescriptionSection";
import ListingLocationSection from "./sections/ListingLocationSection";
import ListingMarketingSection from "./sections/ListingMarketingSection";
import ListingPreviewSection from "./sections/ListingPreviewSection";
import ListingPublishingSection from "./sections/ListingPublishingSection";

/**
 * The listing editor workspace: header, section navigation, the panels and
 * the action footer.
 *
 * Still ONE form with ONE submit and the existing create/edit handler. The
 * navigation is presentation only — it changes which panel is visible and
 * nothing else. No section owns a payload, no section saves on its own, and
 * switching sections issues no request.
 *
 * `editor` and `data` are passed as whole objects on purpose: they are the
 * return values of the two hooks the page already builds, so bundling them
 * here costs nothing and keeps this component from growing a thirty-prop
 * signature. The sections below get narrow, explicit props.
 */
export default function ListingEditorShell({
  editor,
  data,
  images,
  editingListingId,
  activeSection,
  onSectionChange,
}) {
  const editedListing = editingListingId
    ? data.listings.find((l) => l.id === editingListingId)
    : null;

  const objectLabel = editedListing
    ? (() => {
        const d = describeListingObject(editedListing, data.units);
        return `${d.shortId} · ${d.roomLabel}`;
      })()
    : null;

  // `availability_status` is PATCH-only, so it never enters the form. The two
  // sections that merely SHOW it read it from the row the table already holds;
  // a listing being created has none yet and falls back to the model default.
  const availabilityStatus = editedListing?.availability_status || "available";

  const title = editingListingId ? "Listing bearbeiten" : "Neues Listing anlegen";
  const subline = editingListingId
    ? objectLabel
    : "Erstelle ein neues Inserat für deine Website.";
  const secondLine = editingListingId ? (editedListing?.title_de || "") : "";

  const isActive = (id) => activeSection === id;

  // Native constraint validation and the mount-all panels disagree with each
  // other. The browser checks every control in the form on submit, but a
  // control inside a `hidden` panel is not being rendered: it cannot be
  // focused and its message cannot be shown, so the submit is refused with
  // nothing on screen and the save button looks dead.
  //
  // The answer is not to weaken the constraints but to make the offending
  // control reachable — open its panel, focus it, and let the browser say what
  // it wanted to say all along. `data-listing-editor-section` on the panel root
  // is what keeps this generic: no field-to-section table, so a control added
  // to any panel later is covered without touching this handler.
  //
  // `invalid` does not bubble, hence the capture phase.
  const handleInvalidCapture = (event) => {
    const control = event.target;
    const panel = control.closest("[data-listing-editor-section]");

    // Visible control: the browser can do its job, so stay out of the way.
    // This is also the recursion guard — the `reportValidity()` below fires
    // `invalid` a second time, by which point the panel is open and this
    // returns before doing anything again.
    if (!panel || !panel.hidden) return;

    // The browser cannot report on a control it is not rendering. Take this one
    // off its hands instead of leaving it as a silent refusal.
    event.preventDefault();

    // One `invalid` fires per invalid control, in document order. Only the
    // first decides where we navigate, so save always lands the user on the
    // first problem rather than the last panel that happened to complain.
    const form = control.form;
    const firstInvalid = form
      ? Array.from(form.elements).find((el) => el.willValidate && !el.validity.valid)
      : null;
    if (firstInvalid !== control) return;

    onSectionChange(panel.dataset.listingEditorSection);

    // The panel is unhidden only once React has committed, so focus and the
    // native message wait for the next frame. If the switch did not happen
    // after all, report nothing: re-reporting into a still-hidden panel would
    // schedule this same frame again.
    window.requestAnimationFrame(() => {
      if (panel.hidden) return;
      control.focus();
      control.reportValidity();
    });
  };

  return (
    <section
      aria-labelledby="listing-editor-title"
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      <header className="border-b border-border px-6 py-5">
        <h3 id="listing-editor-title" className="m-0 text-[17px] font-bold text-foreground">
          {title}
        </h3>
        {subline ? (
          <p className="m-0 mt-1 text-[13px] text-muted-foreground">{subline}</p>
        ) : null}
        {secondLine ? (
          <p className="m-0 text-[13px] text-muted-foreground">{secondLine}</p>
        ) : null}
      </header>

      <form
        onSubmit={editor.handleSubmitCreate}
        onInvalidCapture={handleInvalidCapture}
        className="px-6 py-5"
      >
        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <ListingEditorNav activeSection={activeSection} onSelect={onSectionChange} />

          <div className="min-w-0">
            <ListingEditorPanel
              id="basic"
              title="Basisinformationen"
              description="Grundlegende Angaben zum Inserat."
              active={isActive("basic")}
            >
              <ListingBasicInfoSection
                form={editor.form}
                setForm={editor.setForm}
                listings={data.listings}
                units={data.units}
                cities={data.cities}
                listingType={editor.listingType}
                editingListingId={editingListingId}
                unitsLoading={data.unitsLoading}
                unitsError={data.unitsError}
                citiesLoading={data.citiesLoading}
                roomsLoading={editor.roomsLoading}
                roomsForSelectedUnit={editor.roomsForSelectedUnit}
                isSelectedUnitColiving={editor.isSelectedUnitColiving}
                formListingTypeAllowed={editor.formListingTypeAllowed}
                onChange={editor.handleChange}
                onListingTypeChange={editor.handleListingTypeChange}
                onUnitChange={editor.handleUnitChange}
                onCityChange={editor.handleCityChange}
              />
            </ListingEditorPanel>

            <ListingEditorPanel
              id="description"
              title="Beschreibung"
              description="Titel und Beschreibungstexte in beiden Sprachen."
              active={isActive("description")}
            >
              <ListingDescriptionSection
                form={editor.form}
                setForm={editor.setForm}
                onChange={editor.handleChange}
              />
            </ListingEditorPanel>

            <ListingEditorPanel
              id="amenities"
              title="Ausstattung"
              description="Preis, Kennzahlen und Ausstattungsmerkmale."
              active={isActive("amenities")}
            >
              <ListingAmenitiesSection
                form={editor.form}
                onChange={editor.handleChange}
                amenitiesDe={editor.amenitiesDe}
                amenitiesEn={editor.amenitiesEn}
                setAmenitiesDe={editor.setAmenitiesDe}
                setAmenitiesEn={editor.setAmenitiesEn}
              />
            </ListingEditorPanel>

            <ListingEditorPanel
              id="marketing"
              title="Marketing Details"
              description="Optional. Leer lassen heisst: erscheint nicht auf der Website."
              active={isActive("marketing")}
            >
              <ListingMarketingSection form={editor.form} onChange={editor.handleChange} />
            </ListingEditorPanel>

            <ListingEditorPanel
              id="location"
              title="Adresse & Lage"
              description="Adresse, öffentliche Lagebeschreibung und Koordinaten."
              active={isActive("location")}
            >
              <ListingLocationSection
                form={editor.form}
                setForm={editor.setForm}
                onChange={editor.handleChange}
                onAddressSelect={editor.handleAddressSelect}
              />
            </ListingEditorPanel>

            <ListingEditorPanel
              id="publishing"
              title="Veröffentlichung"
              description="Sichtbarkeit und Reihenfolge auf der Website."
              active={isActive("publishing")}
            >
              <ListingPublishingSection
                form={editor.form}
                onChange={editor.handleChange}
                availabilityStatus={availabilityStatus}
              />
            </ListingEditorPanel>

            <ListingEditorPanel
              id="preview"
              title="Vorschau"
              description="So liest sich das Inserat als Karte — aus dem aktuellen Formular, ohne Speichern."
              active={isActive("preview")}
            >
              <ListingPreviewSection
                form={editor.form}
                cities={data.cities}
                images={images}
                availabilityStatus={availabilityStatus}
              />
            </ListingEditorPanel>
          </div>
        </div>

        <ListingEditorActions
          editingListingId={editingListingId}
          submitting={editor.submitting}
          createSuccess={editor.createSuccess}
          createError={editor.createError}
          onCancelEdit={editor.handleCancelEdit}
        />
      </form>
    </section>
  );
}
